import { Icon } from '@iconify/react';
import { type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CustomTitleBar from 'src/components/GuideMeTitleBar';
import Button from 'src/components/design-system/Button';
import { IsOfflineSupplierNoShare } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import {
  SupplierWith680And684Permissions,
  SupplierWith680And820Permissions,
} from 'src/sections/manage_suppliers/utils/supplierInfoPermissions';

const useShowSupplierEditGuidePoints = () => {
  const has680And684 = usePermission({ permissions: SupplierWith680And684Permissions });
  const has680And820 = usePermission({ permissions: SupplierWith680And820Permissions });

  return has680And684 || has680And820;
};

/** Ignores Trans-injected children so the icon always renders. */
const GuideFilterIcon = (_props: { children?: ReactNode }) => (
  <span className="inline-flex items-center align-middle bg-[#3F6578] rounded-sm p-1">
    <Icon height="14" width="14" color="#ffffff" icon="mdi:filter" />
  </span>
);

const ViewAndEditSuppliersContent = () => {
  const { t } = useTranslation();
  const showEditGuidePoints = useShowSupplierEditGuidePoints();
  const isOfflineSupplierNoShare = usePermission({
    permissions: IsOfflineSupplierNoShare,
  });

  return (
    <ul className="space-y-1 px-4 font-bold text-[13px]">
      <li className="list-disc font-semibold">{t('manageSuppliersGuide.listDisplaysSuppliers')}</li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="manageSuppliersGuide.filterColumnHeader"
          components={{
            filterIcon: <GuideFilterIcon />,
          }}
        />
      </li>
      <li className="list-disc font-semibold">{t('manageSuppliersGuide.filterBySearch')}</li>
      <li className="list-disc font-semibold">
        {t('manageSuppliersGuide.showAllMapped', { filter: 'Show Mapped' })}
      </li>
      <li className="list-disc font-semibold">
        {t('manageSuppliersGuide.showAllUnmapped', { filter: 'Show Unmapped' })}
      </li>
      <li className="list-disc font-semibold">{t('manageSuppliersGuide.inactiveArchived')}</li>
      {showEditGuidePoints ? (
        <>
          <li className="list-disc font-semibold">
            <Trans
              i18nKey="manageSuppliersGuide.reactivateSuppliers"
              components={{
                editButton: (
                  <Button color="info" className="inline-block" readonly={true} size="md">
                    {null}
                  </Button>
                ),
              }}
            />
          </li>
          <li className="list-disc font-semibold">
            <Trans
              i18nKey="manageSuppliersGuide.setupLogOnly"
              components={{
                editButton: (
                  <Button color="info" className="inline-block" readonly={true} size="md">
                    {null}
                  </Button>
                ),
              }}
            />
          </li>
          {!isOfflineSupplierNoShare ? (
            <li className="list-disc font-semibold">
              {t('manageSuppliersGuide.sharedSupplierEdit')}
            </li>
          ) : null}
        </>
      ) : null}
    </ul>
  );
};

const SupplierActionsContent = () => {
  const showEditGuidePoints = useShowSupplierEditGuidePoints();

  return (
    <ul className="space-y-1 px-4 font-bold text-[13px]">
      {showEditGuidePoints ? (
        <li className="list-disc font-semibold">
          <Trans
            i18nKey="manageSuppliersGuide.createNewSupplier"
            components={{
              createButton: (
                <Button color="success" className="inline-block" readonly={true} size="md">
                  {null}
                </Button>
              ),
            }}
          />
        </li>
      ) : null}
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="manageSuppliersGuide.createPo"
          components={{
            createPoButton: (
              <Button color="primary" className="inline-block" readonly={true} size="md">
                {null}
              </Button>
            ),
          }}
        />
      </li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="manageSuppliersGuide.openCatalog"
          components={{
            openCatalogButton: (
              <Button color="info" className="inline-block" readonly={true} size="md">
                {null}
              </Button>
            ),
          }}
        />
      </li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="manageSuppliersGuide.openPunchout"
          components={{
            openPunchoutButton: (
              <Button color="info" className="inline-block" readonly={true} size="md">
                {null}
              </Button>
            ),
          }}
        />
      </li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="manageSuppliersGuide.viewSupplier"
          components={{
            viewButton: (
              <Button color="info" className="inline-block" readonly={true} size="md">
                {null}
              </Button>
            ),
          }}
        />
      </li>
      {showEditGuidePoints ? (
        <li className="list-disc font-semibold">
          <Trans
            i18nKey="manageSuppliersGuide.editSupplier"
            components={{
              editButton: (
                <Button color="info" className="inline-block" readonly={true} size="md">
                  {null}
                </Button>
              ),
            }}
          />
        </li>
      ) : null}
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="manageSuppliersGuide.programInfo"
          components={{
            programInfoButton: (
              <Button color="info" className="inline-block" readonly={true} size="md">
                {null}
              </Button>
            ),
          }}
        />
      </li>
    </ul>
  );
};

export const useGuideMeSteps = () => {
  const { t } = useTranslation();
  const showEditGuidePoints = useShowSupplierEditGuidePoints();

  return [
    {
      content: <ViewAndEditSuppliersContent />,
      title: (
        <CustomTitleBar
          title={
            showEditGuidePoints
              ? t('Create PO / View and Edit Suppliers')
              : t('Create PO / View Suppliers')
          }
          className="font-bold text-xl text-red-600 max-[425px]:text-base max-[425px]:leading-snug"
        />
      ),
    },
    {
      content: <SupplierActionsContent />,
      title: (
        <CustomTitleBar
          title={
            showEditGuidePoints
              ? t('Create PO / View and Edit Suppliers')
              : t('Create PO / View Suppliers')
          }
          className="font-bold text-xl text-red-600 max-[425px]:text-base max-[425px]:leading-snug"
        />
      ),
    },
  ];
};

export default useGuideMeSteps;
